import React from 'react';
import { ArrowLeft, Globe as GlobeIcon, Users as UsersIcon, Calendar as CalendarIcon, MessageSquare as MessageSquareIcon, Building2 as Building2Icon, ExternalLink as ExternalLinkIcon } from 'lucide-react';
import { companies, people } from '../../data/mockData';
interface CompanyProfileProps {
  companyId: string;
  onNavigate: (page: string, id?: string) => void;
}
export function CompanyProfile({
  companyId,
  onNavigate
}: CompanyProfileProps) {
  const company = companies.find(c => c.id === companyId);
  if (!company) {
    return <div className="text-center py-20 text-slate-500" data-id="element-612">
        Unternehmen nicht gefunden.
      </div>;
  }
  const teamMembers = people.filter(p => p.companyId === company.id);
  return <div className="space-y-6" data-id="element-613">
      <button onClick={() => onNavigate('network-companies')} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors" data-id="element-614">
        <ArrowLeft size={16} data-id="element-615" />
        Zurück zum Verzeichnis
      </button>

      {/* Company Header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-616">
        <div className="h-32 bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center" data-id="element-617">
          <span className="text-7xl drop-shadow-sm" data-id="element-618">{company.logo}</span>
        </div>
        <div className="p-6 md:p-8" data-id="element-619">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-620">
            <div data-id="element-621">
              <div className="flex items-center gap-3 mb-2" data-id="element-622">
                <h1 className="text-2xl font-bold text-slate-900" data-id="element-623">
                  {company.name}
                </h1>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600" data-id="element-624">
                  {company.industry}
                </span>
              </div>
              <p className="text-slate-600 max-w-2xl leading-relaxed" data-id="element-625">
                {company.description}
              </p>
            </div>
            <button onClick={() => onNavigate('messages')} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors shadow-sm whitespace-nowrap" data-id="element-626">
              <MessageSquareIcon size={16} data-id="element-627" />
              Kontakt aufnehmen
            </button>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pt-6 border-t border-slate-100" data-id="element-628">
            <div className="flex items-center gap-3" data-id="element-629">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600" data-id="element-630">
                <UsersIcon size={18} data-id="element-631" />
              </div>
              <div data-id="element-632">
                <p className="text-lg font-bold text-slate-900" data-id="element-633">
                  {company.employeeCount}
                </p>
                <p className="text-xs text-slate-500" data-id="element-634">Mitarbeiter</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-635">
              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600" data-id="element-636">
                <Building2Icon size={18} data-id="element-637" />
              </div>
              <div data-id="element-638">
                <p className="text-lg font-bold text-slate-900" data-id="element-639">
                  {teamMembers.length}
                </p>
                <p className="text-xs text-slate-500" data-id="element-640">Im Netzwerk</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-641">
              <div className="p-2 rounded-lg bg-purple-50 text-purple-600" data-id="element-642">
                <CalendarIcon size={18} data-id="element-643" />
              </div>
              <div data-id="element-644">
                <p className="text-lg font-bold text-slate-900" data-id="element-645">
                  {new Date(company.memberSince).getFullYear()}
                </p>
                <p className="text-xs text-slate-500" data-id="element-646">Partner seit</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-647">
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600" data-id="element-648">
                <GlobeIcon size={18} data-id="element-649" />
              </div>
              <div data-id="element-650">
                <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-id="element-651">
                  Website <ExternalLinkIcon size={12} data-id="element-652" />
                </a>
                <p className="text-xs text-slate-500" data-id="element-653">{company.website}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-654">
        <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-655">
          Leistungen & Angebote
        </h2>
        <div className="flex flex-wrap gap-2" data-id="element-656">
          {company.services.map((service, i) => <span key={i} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-sm font-medium rounded-lg border border-emerald-100" data-id="element-657">
              {service}
            </span>)}
        </div>
      </div>

      {/* Team Members */}
      <div className="space-y-4" data-id="element-658">
        <div className="flex items-center justify-between" data-id="element-659">
          <h2 className="text-lg font-bold text-slate-900" data-id="element-660">
            Team im Netzwerk ({teamMembers.length})
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-661">
          {teamMembers.map(person => <div key={person.id} onClick={() => onNavigate('person-profile', person.id)} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-all cursor-pointer group flex items-center gap-4" data-id="element-662">
              <div className="relative flex-shrink-0" data-id="element-663">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-lg font-bold text-slate-600" data-id="element-664">
                  {person.avatar}
                </div>
                {person.isOnline && <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full" data-id="element-665"></div>}
              </div>
              <div className="min-w-0" data-id="element-666">
                <h3 className="font-bold text-slate-900 group-hover:text-emerald-600 transition-colors truncate" data-id="element-667">
                  {person.firstName} {person.lastName}
                </h3>
                <p className="text-sm text-slate-500 truncate" data-id="element-668">{person.role}</p>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400" data-id="element-669">
                  <span data-id="element-670">{person.eventsAttended} Events</span>
                  <span data-id="element-671">•</span>
                  <span data-id="element-672">{person.connectionsCount} Kontakte</span>
                </div>
              </div>
            </div>)}
        </div>
        {teamMembers.length === 0 && <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center" data-id="element-673">
            <UsersIcon size={40} className="mx-auto text-slate-300 mb-3" data-id="element-674" />
            <p className="text-slate-500" data-id="element-675">
              Noch keine Teammitglieder im Netzwerk registriert.
            </p>
          </div>}
      </div>
    </div>;
}